import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  Alert,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Table
} from 'antd'
import dayjs from 'dayjs'
import { now as tzNow } from '../utils/timezone'

/**
 * 結帳頁的「設定分期」Modal（店內自行分期）。
 *
 * 只負責產出一份分期設定交回結帳頁，不打任何 API ——
 * 排程在這裡用與後端 utils/installmentService.js 相同的規則試算，
 * 真正的計畫等訂單建立成功後才由後端一次建立。
 *
 * 兩個刻意的設計：
 * 1. 頭期款不算在分期本金裡。客人當場付的錢要記在現金／刷卡欄位，
 *    記成分期會讓現金流漏掉當天真正進櫃檯的錢。
 * 2. 手續費對「分期本金」收，不是對整單 —— 客人先付掉的部分店家沒墊錢。
 */

const Summary = styled.div`
  background: #fdf0f3;
  border: 1px solid #f4d6de;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 16px;
  font-size: 13px;
  line-height: 1.8;
  color: #8a5c68;

  strong {
    color: #b4485f;
  }
`

/**
 * 選項文字一律控制在五個字以內。
 *
 * 這個 Select 在 minmax(180px) 的格線裡，長字串收合後會被截成
 * 「每次來店繳一期（日期僅為...」，店家根本看不出自己選了什麼。
 * 需要解釋的話寫在選單下方的提示，不要塞進選項本身。
 */
const FREQUENCY_OPTIONS = [
  { value: 'monthly', label: '每月' },
  { value: 'biweekly', label: '每兩週' },
  { value: 'weekly', label: '每週' },
  { value: 'per_visit', label: '每次來店' }
]

const FREQUENCY_HINT = {
  monthly: '每個月同一天繳一期',
  biweekly: '每兩週繳一期',
  weekly: '每週繳一期',
  per_visit: '到期日只是預估，實際看客人哪天來店'
}

const FREQUENCY_STEP = {
  monthly: { unit: 'month', step: 1 },
  biweekly: { unit: 'day', step: 14 },
  weekly: { unit: 'day', step: 7 },
  per_visit: { unit: 'month', step: 1 }
}

/**
 * 與後端 buildSchedule 同一套規則。
 * 餘數併進「最後一期」—— 讓客人第一期就多付 2 元的觀感差很多。
 */
export const buildScheduleLocal = ({
  principal,
  feeAmount,
  periods,
  frequency,
  firstDueDate,
  downPayment = 0
}) => {
  const total = Math.round(Number(principal) || 0) + Math.round(Number(feeAmount) || 0)
  const count = Math.max(1, Math.min(Number(periods) || 1, 36))
  const { unit, step } = FREQUENCY_STEP[frequency] || FREQUENCY_STEP.monthly
  const first = dayjs(firstDueDate).isValid() ? dayjs(firstDueDate) : tzNow()
  const down = Math.max(0, Math.min(Math.round(Number(downPayment) || 0), total))

  const row = (period, offset, amount) => ({
    period,
    dueDate: first.add(offset * step, unit).format('YYYY-MM-DD'),
    amount
  })

  // 沒有頭期款：整筆平均分，餘數併最後一期
  if (down <= 0) {
    const base = Math.floor(total / count)
    const remainder = total - base * count
    return Array.from({ length: count }, (_, i) =>
      row(i + 1, i, i === count - 1 ? base + remainder : base)
    )
  }

  /**
   * 有頭期款：頭期款就是第 1 期，剩下的錢平均分給後面幾期。
   *
   * 不能讓第 1 期維持平均值、再拿頭期款去沖 —— 店家填 1,270 卻看到
   * 「第 1 期 1,266」，多出來的 4 元被默默挪到第 2 期，對不上他跟客人談的數字。
   */
  if (count <= 1 || down >= total) return [row(1, 0, total)]

  const rest = total - down
  const restCount = count - 1
  const base = Math.floor(rest / restCount)
  const remainder = rest - base * restCount
  return [
    row(1, 0, down),
    ...Array.from({ length: restCount }, (_, i) =>
      row(i + 2, i + 1, i === restCount - 1 ? base + remainder : base)
    )
  ]
}

const InstallmentPlanModal = ({
  open,
  onCancel,
  onConfirm,
  // 應收金額扣掉其他付款方式後還剩多少，用來當分期本金的預設值
  suggestedPrincipal = 0,
  initialValue = null
}) => {
  const [form] = Form.useForm()
  const [principal, setPrincipal] = useState(0)
  const [feeRate, setFeeRate] = useState(0)
  const [feeAmount, setFeeAmount] = useState(0)
  // 店家自己改過手續費金額後就不再被費率覆寫，跟結帳頁的金流手續費同一個邏輯
  const [feeOverridden, setFeeOverridden] = useState(false)
  const [periods, setPeriods] = useState(3)
  // 預設「每次來店」：美業實際上多數是客人下次回診才繳一期，不是固定月繳
  const [frequency, setFrequency] = useState('per_visit')
  // 預設今天：實務上客人多半結帳當天就先付第一期
  const [firstDueDate, setFirstDueDate] = useState(tzNow())
  // 頭期款：今天就收的那一筆。走分期帳本，不寫進訂單的現金欄位（見下方說明）
  const [downPayment, setDownPayment] = useState(0)
  const [downPaymentMethod, setDownPaymentMethod] = useState('cash')

  useEffect(() => {
    if (!open) return
    const init = initialValue || {}
    const nextPrincipal = Number(init.principal) || suggestedPrincipal || 0
    setPrincipal(nextPrincipal)
    setFeeRate(Number(init.feeRate) || 0)
    setFeeAmount(Number(init.feeAmount) || 0)
    setFeeOverridden(false)
    setPeriods(Number(init.periods) || 3)
    setFrequency(init.frequency || 'per_visit')
    setFirstDueDate(
      init.firstDueDate ? dayjs(init.firstDueDate) : tzNow()
    )
    setDownPayment(Number(init.downPayment) || 0)
    setDownPaymentMethod(init.downPaymentMethod || 'cash')
    form.setFieldsValue({
      graceDays: init.graceDays != null ? init.graceDays : 3,
      remarks: init.remarks || ''
    })
  }, [open, initialValue, suggestedPrincipal, form])

  // 費率改變時重算手續費（店家手動改過金額就不動）
  useEffect(() => {
    if (feeOverridden) return
    setFeeAmount(Math.round((principal * feeRate) / 100))
  }, [principal, feeRate, feeOverridden])

  const schedule = useMemo(
    () =>
      buildScheduleLocal({
        principal,
        feeAmount,
        periods,
        frequency,
        firstDueDate,
        downPayment
      }),
    [principal, feeAmount, periods, frequency, firstDueDate, downPayment]
  )

  const totalWithFee = Math.round(principal) + Math.round(feeAmount)

  const handleOk = async () => {
    const extra = await form.validateFields()
    if (principal <= 0) {
      Modal.warning({ title: '分期本金必須大於 0' })
      return
    }
    if (downPayment > totalWithFee) {
      Modal.warning({
        title: '頭期款不能大於分期總額',
        content: `分期總額是 NT$ ${totalWithFee.toLocaleString()}`
      })
      return
    }
    onConfirm({
      principal: Math.round(principal),
      feeRate,
      feeAmount: Math.round(feeAmount),
      periods: schedule.length,
      frequency,
      firstDueDate: dayjs(firstDueDate).format('YYYY-MM-DD'),
      graceDays: Number(extra.graceDays) || 0,
      remarks: extra.remarks || '',
      downPayment: Math.round(downPayment),
      downPaymentMethod,
      schedule
    })
  }

  return (
    <Modal
      title='設定分期（店內自行分期）'
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      okText='套用'
      cancelText='取消'
      width={720}
      destroyOnClose
    >
      <Alert
        type='info'
        showIcon
        style={{ marginBottom: 16 }}
        message='分期總額填「客人總共要用分期繳的錢」，期數就是您跟客人講的期數'
        description='客人今天先繳的那一筆填在下面的「頭期款」，不要另外填到結帳頁的現金欄位 —— 系統會把它記成這筆分期的第 1 筆收款，兩邊都填會讓現金流算到兩次。'
      />

      <Form form={form} layout='vertical'>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12
          }}
        >
          <Form.Item
            label='分期總額'
            tooltip='客人總共要用分期方式繳的錢，含今天要收的頭期款。'
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              prefix='NT$'
              value={principal}
              onChange={(val) => setPrincipal(Number(val) || 0)}
            />
          </Form.Item>

          <Form.Item label='期數'>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              max={36}
              value={periods}
              onChange={(val) => setPeriods(Number(val) || 1)}
            />
          </Form.Item>

          <Form.Item
            label='繳款週期'
            extra={
              <span style={{ fontSize: 12, color: '#a0a0a5' }}>
                {FREQUENCY_HINT[frequency]}
              </span>
            }
          >
            <Select
              value={frequency}
              onChange={setFrequency}
              options={FREQUENCY_OPTIONS}
            />
          </Form.Item>

          <Form.Item
            label='首期付款日'
            tooltip='第一期什麼時候收。多數情況客人結帳當天就先付一期，所以預設是今天。'
          >
            <DatePicker
              style={{ width: '100%' }}
              value={firstDueDate}
              onChange={(val) => setFirstDueDate(val || tzNow())}
              allowClear={false}
            />
          </Form.Item>

          <Form.Item
            label='分期手續費率 (%)'
            tooltip='對分期本金收取。客人先付掉的部分不算在內。'
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={100}
              value={feeRate}
              onChange={(val) => {
                setFeeRate(Number(val) || 0)
                setFeeOverridden(false)
              }}
            />
          </Form.Item>

          <Form.Item
            label='分期手續費金額'
            tooltip='依費率自動計算，可自行修改。這筆錢不計入設計師業績。'
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              prefix='NT$'
              value={feeAmount}
              onChange={(val) => {
                setFeeAmount(Number(val) || 0)
                setFeeOverridden(true)
              }}
            />
          </Form.Item>

          <Form.Item
            label='頭期款（今天就收）'
            tooltip='客人今天先繳的金額。會記成這筆分期的第 1 筆收款，結帳頁的現金欄位不用再填。'
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              prefix='NT$'
              value={downPayment}
              onChange={(val) => setDownPayment(Number(val) || 0)}
            />
          </Form.Item>

          <Form.Item label='頭期款收款方式'>
            <Select
              value={downPaymentMethod}
              onChange={setDownPaymentMethod}
              disabled={downPayment <= 0}
              options={[
                { value: 'cash', label: '現金' },
                { value: 'transfer', label: '匯款' },
                { value: 'cardPayment', label: '刷卡' },
                { value: 'mobilePayment', label: '行動支付' }
              ]}
            />
          </Form.Item>

          <Form.Item
            name='graceDays'
            label='逾期寬限天數'
            tooltip='到期日後幾天內不算逾期。建立後會存成快照，日後改設定不影響這筆。'
            extra={
              frequency === 'per_visit' ? (
                <span style={{ fontSize: 12, color: '#a0a0a5' }}>
                  這個週期不判斷逾期，用不到
                </span>
              ) : undefined
            }
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={60}
              disabled={frequency === 'per_visit'}
            />
          </Form.Item>
        </div>

        <Form.Item name='remarks' label='備註（只有店家看得到）'>
          <Input.TextArea rows={2} maxLength={500} showCount />
        </Form.Item>
      </Form>

      <Summary>
        分期總額 <strong>NT$ {Math.round(principal).toLocaleString()}</strong>
        {feeAmount > 0 && (
          <>
            {' '}＋ 手續費{' '}
            <strong>NT$ {Math.round(feeAmount).toLocaleString()}</strong>
          </>
        )}
        {' '}＝ 客人總共要繳{' '}
        <strong>NT$ {totalWithFee.toLocaleString()}</strong>，分{' '}
        <strong>{schedule.length}</strong> 期
        {downPayment > 0 && (
          <div style={{ marginTop: 4 }}>
            其中今天先收{' '}
            <strong>NT$ {Math.round(downPayment).toLocaleString()}</strong>
            ，剩下{' '}
            <strong>
              NT$ {Math.max(0, totalWithFee - Math.round(downPayment)).toLocaleString()}
            </strong>{' '}
            之後再收。結帳頁的現金欄位不用填這筆。
          </div>
        )}
        {feeAmount > 0 && (
          <div style={{ marginTop: 4, fontSize: 12 }}>
            手續費會加進這張單的應收金額，但不計入業績與抽成。
          </div>
        )}
        {frequency === 'per_visit' && (
          <div style={{ marginTop: 4, fontSize: 12 }}>
            「每次來店繳」的到期日只是預估值，這種計畫不會被列進逾期名單。
          </div>
        )}
      </Summary>

      <Table
        size='small'
        rowKey='period'
        pagination={false}
        scroll={{ y: 240 }}
        dataSource={schedule}
        columns={[
          { title: '期數', dataIndex: 'period', width: 80, render: (v) => `第 ${v} 期` },
          { title: '到期日', dataIndex: 'dueDate', width: 140 },
          {
            title: '金額',
            dataIndex: 'amount',
            align: 'right',
            render: (v) => `NT$ ${Number(v).toLocaleString()}`
          }
        ]}
      />
    </Modal>
  )
}

export default InstallmentPlanModal
